import {
  Col, Form, Input, message, Popover, Row, Select, Switch,
} from 'antd';
import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleStatus, ModuleType } from '../../enums/module.enums';
import useAutoSavingForm from '../../hooks/useAutoSavingForm';
import useLogin from '../../hooks/useLogin';
import NavLinks from '../Module/Form/NavLinks';
import CustomizationCollapse from '../Module/Form/subforms/CustomizationCollapse';
import ModuleTypeForm from '../Module/Form/subforms/ModuleTypeForm';
import OptionsCollapse from '../Module/Form/subforms/OptionsCollapse';
import ProductLaunchForm from '../Module/Form/subforms/ProductLaunchForm';
import ReactionsCollapse from '../Module/Form/subforms/ReactionsCollapse';
import ScheduleCollapse from '../Module/Form/subforms/ModuleSchedule';
import { Label, PageHeader } from './ModuleForm.styled';
import PageHeading from '../common/PageHeading';
import PageControls from '../common/PageControls';
import AutosaveStatus from '../AutosaveStatus/AutosaveStatus';
import { TooltipContentWrapper } from '../common/TooltipBubble/TooltipBubble';
import { useModuleFields } from './ModuleForm.hooks';
import MoodmeterOptionsCollapse from '../Module/Form/subforms/MoodmeterOptionsCollapse';
import MenuOptionsCollapse from '../Module/Form/subforms/MenuOptionsCollapse';
import usePresenterUrl from '../../hooks/presenter/usePresenterUrl';

function ModuleForm({
  type = 'create',
  form,
  onSubmit,
  module = {},
  setSubTypeChanged,
  onOptionUpdate, // Callback for updating options
  onOptionDelete, // Callback for deleting options
  onOptionCreate, // Callback for creating new options
}) {
  // we need user object here
  const { isModerator } = useLogin();
  const { t } = useTranslation();
  const { getFieldDecorator, validateFields, getFieldValue } = form;
  const { Option } = Select;
  const presenterUrl = usePresenterUrl();
  const [moodmeterLabels, setMoodmeterLabels] = useState(module.moodmeter_labels);
  const moduleFields = useModuleFields(getFieldValue('type'));

  // Track which options have already been created to prevent duplicates during autosave
  const [createdOptionIds, setCreatedOptionIds] = useState(new Set());

  // Reset createdOptionIds tracking when module changes
  useEffect(() => {
    setCreatedOptionIds(new Set());
  }, [module?.id]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Flag to track if option creation failed
    let optionCreationFailed = false;

    try {
      const payload = await validateFields();

      if (payload.show_new_comments !== false) {
        payload.show_new_comments = true;
      }

      if (payload.type === ModuleType.Reaction || payload.type === ModuleType.ProductLaunch) {
        payload.show_results = true;
        payload.show_total_count = true;
        payload.price = 0;
        payload.megavote_price = 0;
        payload.megavote_amount = 0;
      }

      // Fallback modules: status is irrelevant; ensure we send a value so API accepts the payload
      if (payload.type === ModuleType.Fallback) {
        payload.status = module.status || ModuleStatus.Live;
      }

      // Validate menu options if this is a menu module
      if (payload.type === 'menu' && payload.menu_options) {
        const menuOptionErrors = [];
        payload.menu_options.forEach((option, index) => {
          if (!option.title || option.title.trim() === '') {
            menuOptionErrors.push(`Menu Option ${index + 1}: Title is required`);
          }
          if (!option.url || option.url.trim() === '') {
            menuOptionErrors.push(`Menu Option ${index + 1}: URL is required`);
          }
        });

        if (menuOptionErrors.length > 0) {
          if (!menuOptionErrors.join('').includes('Title is required') || !menuOptionErrors.join('').includes('URL is required')) {
            message.error(`Please fix the following errors:\n${menuOptionErrors.join('\n')}`);
            return;
          }
        }
      }

      // if type is not 'vote', module cannot be paid, otherwise user can choose
      payload.is_paid = payload.type === ModuleType.Vote ? Boolean(payload.is_paid) : false;
      payload.moodmeter_labels = moodmeterLabels;
      if (payload.timezone) {
        delete payload.timezone;
      }

      // Handle new menu options before submitting the module
      if (type === 'update' && onOptionCreate && payload.menu_options) {
        const newOptions = payload.menu_options.filter((option) => option.id
          && typeof option.id === 'number'
          && option.id < 0
          && !createdOptionIds.has(option.id)
          && option.title && option.title.trim() !== '' // Ensure title is not empty
          && option.url && option.url.trim() !== ''); // Ensure URL is not empty

        if (newOptions.length > 0) {
          try {
            console.log('Creating new menu options with weights:', newOptions.map((o) => ({ title: o.title, weight: o.weight })));
            console.log('Previously created option IDs:', Array.from(createdOptionIds));
            console.log('These options will be created now (not duplicates)');

            // Create new options first
            for (const option of newOptions) {
              console.log(`Creating option with temp ID ${option.id}:`, option.title);
              await onOptionCreate({
                title: option.title,
                subtitle: option.subtitle || '',
                image: option.image || '',
                url: option.url,
                weight: option.weight || 0,
                published: option.published !== false,
              });

              // Mark this option as created to prevent duplicate creation
              setCreatedOptionIds((prev) => new Set([...prev, option.id]));
              console.log(`Option ${option.id} marked as created. Updated set:`, Array.from([...createdOptionIds, option.id]));
            }

            // Remove the temporary IDs from the payload since they'll be replaced with real IDs
            payload.menu_options = payload.menu_options.map((option) => {
              if (option.id && typeof option.id === 'number' && option.id < 0) {
                // Remove the temporary ID so the backend doesn't try to update it
                const { id, ...optionWithoutId } = option;
                return optionWithoutId;
              }
              return option;
            });
          } catch (error) {
            message.error('Failed to create new menu options. Please try again.');
            console.error('Error creating menu options:', error);
            // Set flag to indicate option creation failed
            optionCreationFailed = true;
            // Return early to prevent form submission
            return;
          }
        } else {
          console.log('No new options to create. All options already processed or no new options found.');
          if (payload.menu_options && payload.menu_options.some((opt) => opt.id && opt.id < 0)) {
            const skippedOptions = payload.menu_options.filter((opt) => opt.id && opt.id < 0);
            console.log(
              'Found options with temp IDs but they were skipped:',
              skippedOptions.map((o) => ({
                id: o.id,
                title: o.title,
                url: o.url,
                reason: !o.title || o.title.trim() === '' ? 'empty title'
                  : !o.url || o.url.trim() === '' ? 'empty URL'
                    : createdOptionIds.has(o.id) ? 'already created' : 'unknown',
              })),
            );
          }
        }
      }

      onSubmit(payload);
    } catch (exception) {
      // Only show generic error message if option creation didn't fail
      if (!optionCreationFailed) {
        message.error('Form submission failed. Check below for details');
      }
      console.error('Form submission error:', exception);
    }
  };

  const [autosaveProps, triggerAutosave, autosaveStatus] = useAutoSavingForm(handleSubmit, {
    enabled: type === 'update',
    delay: 1000,
  });

  // Debounced version of triggerAutosave to prevent auto-save on every keystroke
  const [debounceTimer, setDebounceTimer] = useState(null);
  const debouncedTriggerAutosave = useCallback(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    const timer = setTimeout(() => {
      triggerAutosave();
    }, 1000); // 1 second delay
    setDebounceTimer(timer);
  }, [triggerAutosave, debounceTimer]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
  }, [debounceTimer]);

  const moodmeterLabelsChanged = useMemo(() => {
    if (!module.moodmeter_labels) return false;
    if (!moodmeterLabels) return false;
    if (module.moodmeter_labels.length !== moodmeterLabels.length) return true;
    return !module.moodmeter_labels.every(
      (label, index) => moodmeterLabels[index] === label,
    );
  }, [module, moodmeterLabels]);

  useEffect(() => {
    if (!moodmeterLabelsChanged) return;
    triggerAutosave();
  }, [triggerAutosave, moodmeterLabelsChanged]);

  return (
    <div>
      <PageHeader>
        <PageHeading
          title="module"
          type={type}
          name={module.name}
        />
        <AutosaveStatus status={autosaveStatus} />
        <PageControls
          type={type}
          onClickCreate={handleSubmit}
          localizedButtons="modules.buttons"
          presenterUrl={presenterUrl}
        >
          {type === 'update' && (
            <div style={{ marginLeft: 12 }}>
              <NavLinks module={module} />
            </div>
          )}
        </PageControls>
      </PageHeader>
      {!isModerator && (
        <Form onSubmit={handleSubmit} className="module-form" {...autosaveProps}>
          <Row type="flex" gutter={16} justify="space-between">
            <Col md={24} xs={24}>
              <Col xs={12}>
                <ModuleTypeForm
                  form={form}
                  module={module}
                  setSubTypeChanged={setSubTypeChanged}
                  onChange={debouncedTriggerAutosave}
                />
                {moduleFields.title && (
                  <Form.Item label={t('modules.form.title.label')}>
                    {getFieldDecorator('title', {
                      initialValue: module.title,
                      onChange: debouncedTriggerAutosave,
                    })(<Input placeholder={t('modules.form.title.placeholder')} />)}
                  </Form.Item>
                )}
                {moduleFields.subtitle && (
                  <Form.Item label={t('modules.form.subtitle.label')}>
                    {getFieldDecorator('subtitle', {
                      initialValue: module.subtitle,
                      onChange: debouncedTriggerAutosave,
                    })(<Input placeholder={t('modules.form.subtitle.placeholder')} />)}
                  </Form.Item>
                )}
                <CustomizationCollapse
                  form={form}
                  module={module}
                  onChange={debouncedTriggerAutosave}
                />
                <Row type="flex" gutter={16} justify="start" style={{ marginTop: 16 }}>
                  {moduleFields.status && (
                  <Col span={12}>
                    <Form.Item label={t('modules.form.status.label')}>
                      <Popover
                        content={(
                          <TooltipContentWrapper>
                            {t('modules.form.status.tooltip')}
                          </TooltipContentWrapper>
                        )}
                        placement="topLeft"
                        trigger="hover"
                      >
                        {getFieldDecorator('status', {
                          initialValue: module.status || ModuleStatus.Live,
                          rules: [
                            {
                              type: 'enum',
                              enum: [
                                ModuleStatus.NotStarted,
                                ModuleStatus.Live,
                                ModuleStatus.Ended,
                              ],
                            },
                          ],
                          onChange: debouncedTriggerAutosave,
                        })(
                          <Select>
                            {[
                              ModuleStatus.NotStarted,
                              ModuleStatus.Live,
                              ModuleStatus.Ended,
                            ].map((status) => (
                              <Option key={status} value={status}>
                                {t(`modules.form.status.values.${status}`)}
                              </Option>
                            ))}
                          </Select>,
                        )}
                      </Popover>
                    </Form.Item>
                  </Col>
                  )}
                  <Col span={12}>
                    <Form.Item label={t('modules.form.published.label')}>
                      {getFieldDecorator('published', {
                        valuePropName: 'checked',
                        initialValue: module.published || false,
                        onChange: debouncedTriggerAutosave,
                      })(
                        <Switch />,
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <ReactionsCollapse
                  module={module}
                  form={form}
                  onChange={debouncedTriggerAutosave}
                />
              </Col>
              <Col lg={12} xl={8}>
                <ProductLaunchForm
                  form={form}
                  module={module}
                  onChange={debouncedTriggerAutosave}
                />
                <OptionsCollapse
                  form={form}
                  module={module}
                  onChange={debouncedTriggerAutosave}
                />
                <MoodmeterOptionsCollapse
                  form={form}
                  initialValue={moodmeterLabels}
                  onChange={setMoodmeterLabels}
                />
                <MenuOptionsCollapse
                  form={form}
                  module={module}
                  onChange={debouncedTriggerAutosave}
                  onOptionUpdate={onOptionUpdate}
                  onOptionDelete={onOptionDelete}
                  onOptionCreate={onOptionCreate}
                />
                <ScheduleCollapse
                  form={form}
                  module={module}
                  onChange={debouncedTriggerAutosave}
                />
              </Col>
            </Col>
          </Row>

        </Form>
      )}
    </div>
  );
}

export default Form.create()(ModuleForm);
